/**
 * Query Parser for complex search queries
 * Supports:
 * - Field filters: in:keyword, out:keyword
 * - Exact match: exact:phrase
 * - Boolean operators: AND, OR (all caps)
 * - Regular search terms
 */

export type TokenType = 'term' | 'in' | 'out' | 'exact' | 'and' | 'or' | 'lparen' | 'rparen';

export interface Token {
  type: TokenType;
  value: string;
  raw: string;
  position: number;
}

export interface ParsedQuery {
  tokens: Token[];
  hasComplexQuery: boolean;
}

// Token patterns
const TOKEN_PATTERNS: { type: TokenType; pattern: RegExp }[] = [
  { type: 'in', pattern: /^in:(\S+)/i },
  { type: 'out', pattern: /^out:(\S+)/i },
  { type: 'exact', pattern: /^exact:(\S+)/i },
  { type: 'and', pattern: /^AND\b/ },
  { type: 'or', pattern: /^OR\b/ },
  { type: 'lparen', pattern: /^\(/ },
  { type: 'rparen', pattern: /^\)/ },
  { type: 'term', pattern: /^([^\s()]+)/ },
];

/**
 * Tokenize a search query into structured tokens
 */
export function tokenizeQuery(query: string): Token[] {
  const tokens: Token[] = [];
  let position = 0;
  let remaining = query.trim();

  while (remaining.length > 0) {
    // Skip whitespace
    const whitespaceMatch = remaining.match(/^\s+/);
    if (whitespaceMatch) {
      position += whitespaceMatch[0].length;
      remaining = remaining.slice(whitespaceMatch[0].length);
      continue;
    }

    let matched = false;

    for (const { type, pattern } of TOKEN_PATTERNS) {
      const match = remaining.match(pattern);
      if (match) {
        const raw = match[0];
        const value = match[1] ?? raw;
        tokens.push({ type, value, raw, position });
        position += raw.length;
        remaining = remaining.slice(raw.length);
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Skip unrecognized character
      position++;
      remaining = remaining.slice(1);
    }
  }

  return tokens;
}

/**
 * Parse a query string into a structured format
 */
export function parseQuery(query: string): ParsedQuery {
  const tokens = tokenizeQuery(query);
  
  // Check if this is a complex query (has special operators)
  const hasComplexQuery = tokens.some(t => 
    t.type === 'in' || 
    t.type === 'out' || 
    t.type === 'exact' || 
    t.type === 'and' || 
    t.type === 'or'
  );

  return { tokens, hasComplexQuery };
}

/**
 * Get display information for tokens (for visual rendering)
 */
export function getTokenDisplayInfo(type: TokenType): { 
  label: string; 
  color: string; 
  bgColor: string;
  description?: string;
} {
  switch (type) {
    case 'in':
      return {
        label: 'input',
        color: '#10b981', // green
        bgColor: 'rgba(16, 185, 129, 0.15)',
        description: 'Search in input (parameters, request body)',
      };
    case 'out':
      return {
        label: 'output',
        color: '#8b5cf6', // purple
        bgColor: 'rgba(139, 92, 246, 0.15)',
        description: 'Search in output (responses)',
      };
    case 'exact':
      return {
        label: 'exact',
        color: '#f59e0b', // amber
        bgColor: 'rgba(245, 158, 11, 0.15)',
        description: 'Exact match',
      };
    case 'and':
      return {
        label: 'AND',
        color: '#3b82f6', // blue
        bgColor: 'rgba(59, 130, 246, 0.15)',
        description: 'All terms must match',
      };
    case 'or':
      return {
        label: 'OR',
        color: '#ec4899', // pink
        bgColor: 'rgba(236, 72, 153, 0.15)',
        description: 'Any term can match',
      };
    case 'lparen':
    case 'rparen':
      return {
        label: type === 'lparen' ? '(' : ')',
        color: '#64748b', // slate
        bgColor: 'rgba(100, 116, 139, 0.1)',
      };
    default:
      return {
        label: 'text',
        color: '#64748b',
        bgColor: 'transparent',
      };
  }
}

/**
 * Build a search expression from tokens for evaluation
 * This creates a tree structure that can be evaluated against endpoint data
 */
export interface SearchExpression {
  type: 'and' | 'or' | 'term' | 'in' | 'out' | 'exact';
  value?: string;
  left?: SearchExpression;
  right?: SearchExpression;
}

/**
 * Build a search expression tree from tokens
 * Handles operator precedence: () > exact/in/out > AND > OR
 */
export function buildSearchExpression(tokens: Token[]): SearchExpression | null {
  if (tokens.length === 0) return null;

  // Filter out parentheses for now (we'll handle grouping later if needed)
  const filteredTokens = tokens.filter(t => t.type !== 'lparen' && t.type !== 'rparen');
  
  if (filteredTokens.length === 0) return null;

  // Handle OR operator (lowest precedence)
  const orIndex = findOperatorAtTopLevel(filteredTokens, 'or');
  if (orIndex !== -1) {
    const left = buildSearchExpression(filteredTokens.slice(0, orIndex));
    const right = buildSearchExpression(filteredTokens.slice(orIndex + 1));
    // If either side is null, return the other side (or null if both are null)
    if (!left) return right;
    if (!right) return left;
    return { type: 'or', left, right };
  }

  // Handle AND operator
  const andIndex = findOperatorAtTopLevel(filteredTokens, 'and');
  if (andIndex !== -1) {
    const left = buildSearchExpression(filteredTokens.slice(0, andIndex));
    const right = buildSearchExpression(filteredTokens.slice(andIndex + 1));
    // If either side is null, return the other side (or null if both are null)
    if (!left) return right;
    if (!right) return left;
    return { type: 'and', left, right };
  }

  // Single token
  const token = filteredTokens[0];
  if (!token) return null;
  
  if (token.type === 'in' || token.type === 'out' || token.type === 'exact') {
    return { type: token.type, value: token.value };
  }
  
  // Regular term
  return { type: 'term', value: token.value };
}

/**
 * Find an operator at the top level (not nested in parentheses)
 * For now we don't have real nesting, so just find the operator
 */
function findOperatorAtTopLevel(tokens: Token[], operatorType: TokenType): number {
  return tokens.findIndex(t => t.type === operatorType);
}

/**
 * Check if a value matches a search expression
 */
export function matchesExpression(
  expression: SearchExpression | null,
  searchInField: (field: 'input' | 'output' | 'general', term: string, isExact: boolean) => boolean
): boolean {
  if (!expression) return true;

  switch (expression.type) {
    case 'and': {
      if (!expression.left || !expression.right) return false;
      return matchesExpression(expression.left, searchInField) && 
             matchesExpression(expression.right, searchInField);
    }
    case 'or': {
      if (!expression.left || !expression.right) return false;
      return matchesExpression(expression.left, searchInField) || 
             matchesExpression(expression.right, searchInField);
    }
    case 'in':
      return searchInField('input', expression.value!, false);
    case 'out':
      return searchInField('output', expression.value!, false);
    case 'exact':
      return searchInField('general', expression.value!, true);
    case 'term':
      return searchInField('general', expression.value!, false);
    default:
      return false;
  }
}

/**
 * Format tokens back to a readable string (for display purposes)
 */
export function tokensToString(tokens: Token[]): string {
  return tokens.map(t => t.raw).join(' ');
}
